import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import { type ProductEntity, type SprintEntity } from '@data/utils/types';
import { fetchProduct } from '@data/api/products';
import { useListSprints } from '@data/hooks/sprints';
import * as queryKeys from '@data/constants/keys';
import { fullDate } from '@app/utils/helpers/date-utils';
import { limitConcurrency } from '@app/utils/helpers/limitConcurrency';

import { versionsFor } from '../products/productDetail/productVersions';
import { todayKey } from '../products/productDetail/useProductVersions';
import { hoursByMember } from '../products/productDetail/spend';
import { readFractionalHours, readRoster } from '../products/productDetail/fractional';
import { readDiscount } from '../products/productDetail/discounts';
import { fixedChargeIn } from '../products/productDetail/pricing';
import { retentionLine } from '../products/productDetail/retention';
import { readBudget, sumCosts, teamAt } from '../products/productDetail/budget';
import { versionTeams } from '../products/productDetail/useVersionTeams';
import { productStoreQuery } from '../products/productDetail/useProductStore';
import { sprintRevenue, type ProductRevenue } from './revenuePricing';
import { productReportsQuery } from './productReports';
import { reportsBySprint } from './sprintReports';

export { MAX_PRICED } from './revenuePricing';

/** The same ceiling the exports panel reads at, and for the same reason: the connections behind them. */
const READS_AT_ONCE = 4;

/** A handful of reads per product whatever the span, through one queue. */
export const useProductRevenue = (
  products: { id: string; name: string }[],
  sprints: SprintEntity[],
): { revenue: ProductRevenue[]; isLoading: boolean; isError: boolean } => {
  // One queue for every read here: the API holds a database connection per request, and this fans out.
  const queue = useMemo(() => limitConcurrency(READS_AT_ONCE), []);
  // The page's own list carries hours and a name; a rate needs to know who was on it.
  const productQueries = useQueries({
    queries: products.map(product => ({
      queryKey: [queryKeys.PRODUCT, product.id],
      queryFn: () => queue(() => fetchProduct(product.id)),
      staleTime: 60_000,
    })),
  });

  // Rates, discounts and bought-in hours live in each product's store, which has to be read before anything prices.
  const storeQueries = useQueries({
    queries: products.map(product => {
      const query = productStoreQuery(product.id);
      return { ...query, queryFn: () => queue(query.queryFn) };
    }),
  });

  // The whole span in one read per product, sorted into sprints here.
  const reportQueries = useQueries({
    queries: products.map(product => productReportsQuery(product.id, sprints, queue)),
  });

  // Every sprint, not only those on screen: a fixed price is spread from the version's first one.
  const { data: sprintList } = useListSprints({ limit: 400 });
  const allSprints = useMemo(() => sprintList?.items ?? [], [sprintList]);

  // The query objects are new on every render, so the memo keys on when the data last changed instead.
  const stamps = [...productQueries, ...storeQueries, ...reportQueries].map(query => query.dataUpdatedAt).join(',');
  const revenue = useMemo(
    () => {
      // Keyed as the money tabs key them, off the sprint's own start date rather than a day key.
      const rosterYears = [...new Set(sprints.map(sprint => sprint.startDate.slice(0, 4)))];
      return products.map((product, index) => {
        const entity = productQueries[index]?.data as ProductEntity | undefined;
        // From the store, and via versionsFor: a product nobody has opened the tab on has no version stored.
        const teams = versionTeams(entity, versionsFor(product.id, todayKey()));
        // A roster belongs to one year and these sprints may cross one, so every year on screen is read.
        const roster = rosterYears.flatMap(year => readRoster(product.id, year));

        const bySprint = reportsBySprint(reportQueries[index]?.data?.items ?? [], sprints, { incidentWork: false });
        const reportsIn = (sprint: SprintEntity) => bySprint[sprint.id] ?? [];

        return {
          id: product.id,
          name: product.name,
          hoursBySprint: Object.fromEntries(
            sprints.map(sprint => [
              sprint.id,
              sumCosts([
                ...Object.values(hoursByMember(reportsIn(sprint))),
                ...Object.values(readFractionalHours('spend', product.id, sprint.id)),
              ]),
            ]),
          ),
          bySprint: Object.fromEntries(
            sprints.map(sprint => {
              const reports = reportsIn(sprint);

              const day = fullDate(sprint.startDate);
              const net = sprintRevenue(
                teams,
                day,
                hoursByMember(reports),
                { hours: readFractionalHours('spend', product.id, sprint.id), roster },
                readDiscount(product.id, sprint.id)?.amount ?? 0,
                fixedChargeIn(teamAt(teams, day), sprint.id, allSprints),
              );
              return [sprint.id, net - retentionLine(product.id, teamAt(teams, day), sprint.id, net)];
            }),
          ),
          projectedHoursBySprint: Object.fromEntries(
            sprints.map(sprint => [
              sprint.id,
              sumCosts([
                ...Object.values(readBudget(product.id, sprint.id)?.hours ?? {}),
                ...Object.values(readFractionalHours('budget', product.id, sprint.id)),
              ]),
            ]),
          ),
          // A projection is the work alone: discounts, fixed prices and retention are about bills sent.
          projectedBySprint: Object.fromEntries(
            sprints.map(sprint => [
              sprint.id,
              sprintRevenue(teams, fullDate(sprint.startDate), readBudget(product.id, sprint.id)?.hours ?? {}, {
                hours: readFractionalHours('budget', product.id, sprint.id),
                roster,
              }),
            ]),
          ),
        };
      });
    },
    // Keyed on what the products are, not the array: the caller builds a fresh one every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [products.map(product => `${product.id}:${product.name}`).join(), sprints, stamps, allSprints],
  );

  return {
    revenue,
    isLoading: [...productQueries, ...storeQueries, ...reportQueries].some(query => query.isLoading),
    isError: [...productQueries, ...storeQueries, ...reportQueries].some(query => query.isError),
  };
};
